/**
 * Deal mutation hooks using generic factory
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { dealApi } from '@/lib/api/deals.api';
import { queryKeys } from '../query-keys';
import { createResourceMutations } from './createResourceMutations';
import { dealModuleCountsKey } from '../useDealModuleCounts';
import { toast } from 'sonner';
import type { Deal, CreateDeal, UpdateDeal } from '@scrambl/shared';

/**
 * Generate all deal mutation hooks using the factory
 */
const mutations = createResourceMutations<Deal, CreateDeal, UpdateDeal>({
  resourceName: 'Deal',
  resourceNameSingular: 'deal',
  queryKeyFactory: queryKeys.deals,
  api: {
    create: (data: CreateDeal) => dealApi.createDeal(data),
    update: (id: string, data: UpdateDeal) => dealApi.updateDeal(id, data),
    updateField: (id: string, field: string, value: unknown) => {
      const updateData = { [field]: value } as UpdateDeal;
      return dealApi.updateDeal(id, updateData);
    },
    delete: (id: string) => dealApi.deleteDeal(id),
  },
  enableRealtime: false,
  deferredEntityType: 'deal',
  invalidation: {
    onCreate: { invalidateRelated: [[...dealModuleCountsKey]] },
    onDelete: { invalidateRelated: [[...dealModuleCountsKey]] },
  },
});

/**
 * Export individual hooks for backward compatibility
 */
export const useCreateDealMutation = mutations.useCreateMutation;
export const useUpdateDealMutation = mutations.useUpdateMutation;
export const useUpdateDealFieldMutation = mutations.useUpdateFieldMutation!;
export const useDeleteDealMutation = mutations.useDeleteMutation;

/**
 * Bulk delete deals mutation hook
 */
export function useBulkDeleteDealsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids: string[]) => dealApi.bulkDeleteDeals(ids),
    onSuccess: (result) => {
      toast.success(`${result.deletedCount} deal${result.deletedCount === 1 ? '' : 's'} deleted`);
      // Invalidate deals list and module counts
      queryClient.invalidateQueries({ queryKey: queryKeys.deals.lists() });
      queryClient.invalidateQueries({ queryKey: dealModuleCountsKey });
    },
    onError: (error) => {
      toast.error('Failed to delete deals');
      console.error('Bulk delete deals error:', error);
    },
  });
}
